import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useId} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {doNotTranslate} from "@repo/i18n";
import {RequestInfo} from "@repo/shared-core";

import {styles as sharedStyles} from "./constants.ts";
import {getAllCountries} from "./countries.ts";

type CountryDropdownProps = {
    /**
     * The currently selected country code.
     */
    value: string | null | undefined;
    /**
     * A callback to be called when the selected country changes.
     */
    onChange: (countryCode: string) => void;
    /**
     * The label text for the dropdown. This text will also be used as the
     * placeholder and aria-label if `compactLayout` is true.
     */
    labelText?: string;
    /**
     * Whether to use the compact layout. This will hide the labels and reduce
     * the spacing between elements. The fields label text will be set to the
     * placeholder text, and aria-label will be set to the same for
     * accessibility.
     */
    compactLayout?: boolean;
    /**
     * Whether to show the country flag in the country dropdown.
     */
    showCountryFlag?: boolean;
};

export const translateIfOther = (input: string) =>
    input === "OTHER" ? t`Other` : input;

export function CountryDropdown({
    value,
    onChange,
    labelText = t`Country`,
    compactLayout,
    showCountryFlag,
}: CountryDropdownProps): React.ReactElement {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    let countries = getAllCountries();
    if (RequestInfo.kaLocale === "lol") {
        countries = countries.concat([["LOL", doNotTranslate`Lolcat`]]);
    }
    const countrySelectId = useId();

    return (
        <View>
            {!compactLayout && (
                <LabelMedium
                    style={sharedStyles.fieldLabel}
                    tag="label"
                    htmlFor={countrySelectId}
                >
                    {labelText}
                </LabelMedium>
            )}
            <SingleSelect
                id={countrySelectId}
                placeholder={compactLayout ? labelText : t`e.g., Afghanistan`}
                isFilterable={true}
                selectedValue={value}
                onChange={(country) => {
                    onChange(country);
                    publishAnalyticsEvent({
                        eventType: "SchoolPickerCountrySelected",
                        eventSchemaVersion: 2,
                        countryCode: country,
                    });
                }}
                style={styles.dropdown}
                dropdownStyle={styles.dropdown}
                testId="country-dropdown"
                aria-label={compactLayout ? labelText : undefined}
            >
                {countries.map(([code, translatedName]: [any, any]) => (
                    <OptionItem
                        key={code}
                        value={code}
                        label={translateIfOther(translatedName)}
                        testId="country-option"
                        leftAccessory={
                            showCountryFlag ? (
                                <span aria-hidden>{getFlagEmoji(code)}</span>
                            ) : null
                        }
                    />
                ))}
            </SingleSelect>
        </View>
    );
}

function getFlagEmoji(countryCode: string) {
    const codePoints = countryCode
        .toUpperCase()
        .split("")
        .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}

const styles = StyleSheet.create({
    dropdown: {
        width: "100%",
        // HACK(yash): There are some inherited css properties on this page
        // which create a mismatch in expectations from wonder-blocks default
        // styling and what is rendered on the page. This attribute fixes that
        // by forcing the default wonder-blocks styles on the component.
        color: "initial",
    },
});
